import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Paper, TextField, Button,
  Grid, CircularProgress, Alert, Card, CardContent
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const Settings = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [businessInfo, setBusinessInfo] = useState({
    name: '',
    description: '',
    phone: '',
    email: '',
    website: ''
  });

  // State for phone system information
  const [phoneSystemInfo, setPhoneSystemInfo] = useState({
    twilioNumber: '',
    businessNumber: '',
    carrier: '',
    activationCode: '',
    deactivationCode: ''
  });

  // Fetch business data on component mount
  useEffect(() => {
    const fetchBusinessData = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const businessDoc = await getDoc(doc(db, 'businesses', currentUser.uid));

        if (businessDoc.exists()) {
          const data = businessDoc.data();
          console.log('Fetched business data:', data);

          // Extract business info
          const info = data.businessInfo || {};
          const phoneData = data.phoneData || {};

          setBusinessInfo({
            name: info.name || '',
            description: info.description || '',
            phone: phoneData.phoneNumber || phoneData.businessNumber || info.phone || '',
            email: info.email || '',
            website: info.website || ''
          });

          // Extract phone system information
          const carrierDetails = phoneData.carrierDetails || {};
          const twilioNumber = phoneData.twilioNumber || data.twilioNumber || '';
          const businessNumber = phoneData.businessNumber || phoneData.phoneNumber || '';

          // Construct activation code
          let activationCode = '';
          if (carrierDetails.activationPrefix && twilioNumber) {
            activationCode = `${carrierDetails.activationPrefix}${twilioNumber}${carrierDetails.activationSuffix || ''}`;
          } else if (carrierDetails.activateCode) {
            activationCode = carrierDetails.activateCode;
          }

          setPhoneSystemInfo({
            twilioNumber,
            businessNumber,
            carrier: phoneData.carrier || carrierDetails.name || '',
            activationCode,
            deactivationCode: carrierDetails.deactivateCode || ''
          });
        }
      } catch (err) {
        console.error('Error fetching business data:', err);
        setError('Failed to load business information');
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [currentUser]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBusinessInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Save business info
  const handleSave = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      setError('You must be logged in to save changes');
      return;
    }

    // Validate name is not empty
    if (!businessInfo.name.trim()) {
      setError('Business name is required');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess(false);

    try {
      const businessRef = doc(db, 'businesses', currentUser.uid);

      // Get current data first to preserve other fields
      const businessDoc = await getDoc(businessRef);

      if (!businessDoc.exists()) {
        throw new Error('Business document not found');
      }

      const currentData = businessDoc.data();

      // Update only the businessInfo field
      await updateDoc(businessRef, {
        businessInfo: {
          ...currentData.businessInfo, // Keep existing fields
          name: businessInfo.name.trim(),
          description: businessInfo.description.trim(),
          phone: businessInfo.phone.trim(),
          email: businessInfo.email.trim(),
          website: businessInfo.website.trim(),
          updatedAt: new Date().toISOString()
        }
      });

      setSuccess(true);

      // Auto navigate back to dashboard after success
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      console.error('Error saving business info:', err);
      setError('Failed to save business information: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading your business information...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          Back to Dashboard
        </Button>
        <Typography variant="h4" component="h1">
          Business Settings
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Business information updated successfully!
        </Alert>
      )}

      {/* Business Information Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Business Information
        </Typography>

        <Box component="form" onSubmit={handleSave}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Business Name"
                name="name"
                value={businessInfo.name}
                onChange={handleInputChange}
                required
                margin="normal"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={businessInfo.phone}
                onChange={handleInputChange}
                margin="normal"
                placeholder="+12345678900"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={businessInfo.email}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Website"
                name="website"
                value={businessInfo.website}
                onChange={handleInputChange}
                margin="normal"
                placeholder="https://yourwebsite.com"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={businessInfo.description}
                onChange={handleInputChange}
                multiline
                rows={4}
                margin="normal"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Subscription Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Subscription Management
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" paragraph>
            Manage your subscription plan, billing information, and payment methods.
          </Typography>

          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`/subscription/${currentUser.uid}`)}
          >
            Manage Subscription
          </Button>
        </Box>
      </Paper>

      {/* Phone System Information Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Phone System Information
        </Typography>

        <Box sx={{ mt: 2 }}>
          {phoneSystemInfo.twilioNumber ? (
            <>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2 }}>
                Your New Business Line: {phoneSystemInfo.twilioNumber}
              </Typography>

              <Typography variant="h6" sx={{ mt: 2 }}>
                Forwarding From: {phoneSystemInfo.businessNumber}
              </Typography>

              <Typography variant="h6" sx={{ mt: 2 }}>
                Carrier: {phoneSystemInfo.carrier}
              </Typography>

              {phoneSystemInfo.activationCode && (
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold', mt: 2 }}>
                  Activation Code: {phoneSystemInfo.activationCode}
                </Typography>
              )}

              {phoneSystemInfo.deactivationCode && (
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Deactivation Code: {phoneSystemInfo.deactivationCode}
                </Typography>
              )}

              <Typography variant="body1" sx={{ mt: 3, fontStyle: 'italic' }}>
                To activate call forwarding, dial the activation code on your phone.
                When you miss a call, it will be forwarded to our system which will automatically
                text the caller and answer their questions.
              </Typography>
            </>
          ) : (
            <Typography variant="body1" color="text.secondary">
              No phone system information available. Please complete the onboarding process to set up your business line.
            </Typography>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default Settings;